import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Input, Switch, Button, Checkbox, Tooltip, Spin } from 'antd';
import {
	SearchOutlined,
	MoreOutlined,
	EditOutlined,
	SendOutlined,
	ReloadOutlined,
	SelectOutlined,
	CloseOutlined,
	UserAddOutlined,
	ThunderboltOutlined,
	MessageOutlined,
	FilterOutlined,
	CheckCircleOutlined,
} from '@ant-design/icons';
import { MessagesListProps } from '@/types/TTextingPlatform';
import useOnClickOutside from 'src/Hooks/useOnClickOutside';

const MessagesList: React.FC<MessagesListProps> = ({
	msgs,
	filterSearch,
	setFilterSearch,
	filteredUnreplied,
	setFilteredUnreplied,
	selectedClient,
	setSelectedClient,
	savedContacts,
	provisionedNumber,
	dontAutoReplyToTheseNumbers,
	renderPSTDateTruncated,
	setContactAddingNumber,
	setCreateContactVal,
	isInSelectMode,
	setIsInSelectMode,
	selectedMsgs,
	setSelectedMsgs,
	setIsBulkModalActive,
	setIsBulkAIModalActive,
	getTextingConvos,
	textingGk,
	themeColor,
	hasMoreConversations,
	isLoadingMore,
	loadMoreConversations,
}) => {
	const [isMsgMenuActive, setIsMsgMenuActive] = useState<boolean>(false);
	const [isBadgeActive, setIsBadgeActive] = useState<boolean>(true);
	const dropdownRef = useRef<HTMLDivElement>(null);
	const scrollContainerRef = useRef<HTMLDivElement>(null);

	useOnClickOutside(dropdownRef, () => setIsMsgMenuActive(false));

	const handleScroll = useCallback(() => {
		if (!scrollContainerRef.current || !hasMoreConversations || isLoadingMore) {
			return;
		}

		const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current;
		const scrollPercentage = (scrollTop + clientHeight) / scrollHeight;

		if (scrollPercentage > 0.9) {
			loadMoreConversations?.();
		}
	}, [hasMoreConversations, isLoadingMore, loadMoreConversations]);

	useEffect(() => {
		const scrollContainer = scrollContainerRef.current;
		if (scrollContainer) {
			scrollContainer.addEventListener('scroll', handleScroll);
			return () => scrollContainer.removeEventListener('scroll', handleScroll);
		}
	}, [handleScroll]);

	const handleMessageClick = (clientNumber: string) => {
		if (isInSelectMode) {
			if (selectedMsgs.includes(clientNumber)) {
				setSelectedMsgs((prev) => prev.filter((num) => num !== clientNumber));
			} else {
				setSelectedMsgs((prev) => [...prev, clientNumber]);
			}
		} else {
			setSelectedClient(clientNumber);
		}
	};

	const filteredMessages = msgs.filter((el) => {
		const contactNameOrNumber =
			(savedContacts && savedContacts[el.client_number]) || el.client_number || '';

		if (filteredUnreplied) {
			if (
				el?.messages?.length === 0 ||
				el.messages[el.messages.length - 1].from !== provisionedNumber
			) {
				return false;
			}
		}

		if (filterSearch) {
			return (
				el.client_number.includes(filterSearch) ||
				contactNameOrNumber.toLowerCase().includes(filterSearch.toLowerCase())
			);
		}

		return true;
	});

	const formatMessagePreview = (message: string) => {
		if (!message) {
			return 'No messages yet';
		}
		return message.length > 35 ? `${message.slice(0, 35)}...` : message;
	};

	const getInitials = (name: string) => {
		if (!name) {
			return '?';
		}
		const words = name.split(' ');
		if (words.length === 1) {
			return words[0].charAt(0).toUpperCase();
		}
		return (words[0].charAt(0) + words[words.length - 1].charAt(0)).toUpperCase();
	};

	return (
		<div className="w-full sm:w-80 bg-white border-r border-gray-200 flex flex-col h-full">
			<div className="px-3 sm:px-4 py-2 sm:py-3 bg-white border-b border-gray-100 shadow-sm">
				<div className="flex items-center justify-between mb-2 sm:mb-3">
					<div className="flex items-center gap-2 sm:gap-3">
						<MessageOutlined className="text-lg sm:text-xl" style={{ color: themeColor }} />
						<div className="flex items-center gap-2">
							<span className="text-base sm:text-lg font-semibold text-gray-800">Messages</span>
						</div>
					</div>

					<div className="relative" ref={dropdownRef}>
						<Button
							type="text"
							shape="circle"
							icon={<MoreOutlined className="text-gray-600" />}
							onClick={() => setIsMsgMenuActive(!isMsgMenuActive)}
							className="hover:bg-gray-100 border-0 shadow-none"
							size="large"
						/>

						{isMsgMenuActive && (
							<div className="absolute right-0 top-full mt-2 w-48 sm:w-52 bg-white rounded-xl shadow-lg border border-gray-100 z-50 overflow-hidden">
								{textingGk && (
									<>
										<div
											className="px-3 sm:px-4 py-2 sm:py-3 hover:bg-gray-50 cursor-pointer flex items-center gap-2 sm:gap-3 transition-colors"
											onClick={() => {
												setSelectedClient('');
												setIsMsgMenuActive(false);
											}}
										>
											<EditOutlined style={{ color: themeColor }} />
											<span className="font-medium text-gray-700 text-sm sm:text-base">
												New Chat
											</span>
										</div>
										<div
											className="px-3 sm:px-4 py-2 sm:py-3 hover:bg-gray-50 cursor-pointer flex items-center gap-2 sm:gap-3 transition-colors"
											onClick={() => {
												setIsBulkModalActive(true);
												setIsMsgMenuActive(false);
											}}
										>
											<SendOutlined style={{ color: themeColor }} />
											<span className="font-medium text-gray-700 text-sm sm:text-base">
												Broadcast Message
											</span>
										</div>
									</>
								)}
								<div
									className="px-3 sm:px-4 py-2 sm:py-3 hover:bg-gray-50 cursor-pointer flex items-center gap-2 sm:gap-3 transition-colors"
									onClick={() => {
										getTextingConvos();
										setIsMsgMenuActive(false);
									}}
								>
									<ReloadOutlined style={{ color: themeColor }} />
									<span className="font-medium text-gray-700 text-sm sm:text-base">Refresh</span>
								</div>
								<div
									className="px-3 sm:px-4 py-2 sm:py-3 hover:bg-gray-50 cursor-pointer flex items-center gap-2 sm:gap-3 transition-colors"
									onClick={() => {
										setIsInSelectMode(true);
										setIsMsgMenuActive(false);
									}}
								>
									<SelectOutlined style={{ color: themeColor }} />
									<span className="font-medium text-gray-700 text-sm sm:text-base">
										Select Chats
									</span>
								</div>
							</div>
						)}
					</div>
				</div>

				{isBadgeActive && (
					<div className="mb-2 sm:mb-3">
						<div className="flex items-start gap-2 p-2 bg-blue-50 border border-blue-200 rounded-lg">
							<div className="flex-1">
								<p className="text-xs text-blue-700">
									All texts are sent from your Receptionist Number, which is{' '}
									<span className="font-medium">{provisionedNumber}</span>
								</p>
							</div>
							<Button
								type="text"
								icon={<CloseOutlined />}
								onClick={() => setIsBadgeActive(false)}
								className="text-blue-500 hover:bg-blue-100 p-0 border-0 shadow-none"
								size="small"
							/>
						</div>
					</div>
				)}

				<div className="flex items-center justify-between mb-2 sm:mb-3">
					<div className="flex items-center gap-2">
						<FilterOutlined className="text-gray-400 text-xs sm:text-sm" />
						<span className="text-xs sm:text-sm text-gray-600">Show only unread</span>
					</div>
					<Switch
						checked={filteredUnreplied}
						onChange={setFilteredUnreplied}
						size="small"
						style={{ backgroundColor: filteredUnreplied ? themeColor : undefined }}
					/>
				</div>

				<div className="relative">
					<Input
						placeholder="Search conversations..."
						prefix={<SearchOutlined className="text-gray-400" />}
						value={filterSearch}
						onChange={(e) => setFilterSearch(e.target.value)}
						className="rounded-md border-gray-200 bg-gray-50 hover:bg-white focus:bg-white transition-colors text-sm sm:text-base"
						size="large"
					/>
				</div>
			</div>

			{isInSelectMode && (
				<div className="px-3 sm:px-4 py-2 sm:py-3 bg-blue-50 border-b border-blue-100">
					<div className="flex items-center justify-between">
						<div className="flex items-center gap-2 sm:gap-3">
							<Button
								type="text"
								shape="circle"
								icon={<CloseOutlined />}
								onClick={() => {
									setIsInSelectMode(false);
									setSelectedMsgs([]);
								}}
								className="hover:bg-blue-100 text-blue-600"
								size="small"
							/>
							<span className="text-xs sm:text-sm font-medium text-blue-800">
								{selectedMsgs.length === 0 ? 'Select chats' : `${selectedMsgs.length} selected`}
							</span>
						</div>
						{selectedMsgs.length > 0 && (
							<Tooltip title="Manage AI settings for selected chats">
								<Button
									type="primary"
									shape="circle"
									icon={<ThunderboltOutlined />}
									onClick={() => setIsBulkAIModalActive(true)}
									style={{ backgroundColor: themeColor, borderColor: themeColor }}
									size="small"
								/>
							</Tooltip>
						)}
					</div>
				</div>
			)}

			<div
				ref={scrollContainerRef}
				className="flex-1 overflow-y-auto"
				style={{ scrollBehavior: 'smooth' }}
			>
				{filteredMessages.map((el, idx) => {
					const contactNameOrNumber =
						(savedContacts && savedContacts[el.client_number]) || el.client_number || '';
					const isSelected = isInSelectMode && selectedMsgs.includes(el.client_number);
					const isActiveChat = selectedClient === el.client_number;
					const hasAIAutoReply = !dontAutoReplyToTheseNumbers.includes(el.client_number);
					const lastMessage = el.messages.length > 0 ? el.messages[el.messages.length - 1] : null;
					const isContactSaved = savedContacts && savedContacts[el.client_number];
					const fullMessage = lastMessage?.message || 'No messages yet';

					return (
						<div
							key={`chat-${idx}`}
							className={`px-2 sm:px-3 py-2 sm:py-2.5 cursor-pointer transition-all duration-200 hover:bg-gray-50 ${
								isSelected ? 'bg-blue-50 border-l-4 border-l-blue-400' : ''
							} ${isActiveChat ? 'bg-slate-100 border-l-4 border-l-slate-500' : ''}`}
							onClick={() => handleMessageClick(el.client_number)}
						>
							<div className="flex items-center gap-2 sm:gap-3">
								{isInSelectMode && (
									<div className="flex-shrink-0">
										<Checkbox
											checked={isSelected}
											className="rounded-full"
											style={{ accentColor: themeColor }}
										/>
									</div>
								)}

								<div className="flex-shrink-0">
									<div
										className={`w-9 h-9 sm:w-11 sm:h-11 rounded-full flex items-center justify-center text-white font-medium text-xs sm:text-sm ${
											isContactSaved ? 'shadow-sm' : 'bg-gradient-to-br from-gray-400 to-gray-500'
										}`}
										style={{
											backgroundColor: isContactSaved ? `${themeColor}` : undefined,
										}}
									>
										{isContactSaved ? getInitials(contactNameOrNumber) : '👤'}
									</div>
								</div>

								<div className="flex-1 min-w-0">
									<div className="flex items-start justify-between mb-0.5">
										<div className="flex items-center gap-1 sm:gap-2 flex-1 min-w-0">
											<h3 className="font-semibold text-gray-900 truncate text-xs sm:text-sm">
												{contactNameOrNumber}
											</h3>
											{hasAIAutoReply && (
												<Tooltip title="AI auto-reply is enabled for this contact" placement="top">
													<ThunderboltOutlined className="text-xs text-amber-500 flex-shrink-0" />
												</Tooltip>
											)}
										</div>
										<span className="text-xs text-gray-500 flex-shrink-0 ml-1 sm:ml-2">
											{lastMessage ? renderPSTDateTruncated(lastMessage.timestamp) : ''}
										</span>
									</div>

									<div className="flex items-center justify-between">
										<Tooltip title={fullMessage} placement="bottomLeft" mouseEnterDelay={0.5}>
											<p className="text-xs text-gray-600 truncate flex-1 cursor-help">
												{formatMessagePreview(fullMessage)}
											</p>
										</Tooltip>

										<div className="flex items-center gap-1 sm:gap-1.5 ml-1 sm:ml-2 flex-shrink-0">
											<Tooltip
												title={isContactSaved ? 'Edit contact name' : 'Add contact name'}
												placement="top"
											>
												<Button
													type="text"
													icon={<UserAddOutlined />}
													onClick={(e) => {
														e.stopPropagation();
														setContactAddingNumber(el.client_number);
														setCreateContactVal(isContactSaved ? contactNameOrNumber : '');
													}}
													className="text-gray-400 hover:text-blue-500 hover:bg-blue-50 p-1 border-0 shadow-none h-5 w-5 sm:h-6 sm:w-6 flex items-center justify-center"
													size="small"
												/>
											</Tooltip>

											{isActiveChat && (
												<CheckCircleOutlined className="text-slate-500 text-xs sm:text-sm" />
											)}
										</div>
									</div>
								</div>
							</div>
						</div>
					);
				})}

				{isLoadingMore && (
					<div className="flex justify-center items-center py-4">
						<Spin size="small" />
						<span className="ml-2 text-sm text-gray-500">Loading more conversations...</span>
					</div>
				)}

				{filteredMessages.length === 0 && (
					<div className="flex flex-col items-center justify-center py-8 sm:py-12 px-4 text-center">
						<div className="w-12 h-12 sm:w-16 sm:h-16 bg-gray-100 rounded-full flex items-center justify-center mb-3 sm:mb-4">
							<MessageOutlined className="text-xl sm:text-2xl text-gray-400" />
						</div>
						<h3 className="text-base sm:text-lg font-medium text-gray-600 mb-2">No chats found</h3>
						<p className="text-xs sm:text-sm text-gray-500 max-w-48">
							{filterSearch || filteredUnreplied
								? 'Try adjusting your search or filters'
								: 'Your conversations will appear here'}
						</p>
						{filterSearch && (
							<Button
								type="link"
								onClick={() => setFilterSearch('')}
								className="mt-2 text-xs sm:text-sm"
								style={{ color: themeColor }}
							>
								Clear search
							</Button>
						)}
					</div>
				)}
			</div>
		</div>
	);
};

export default MessagesList;
